import { and, eq, type InitialQueryBuilder, isNull, not } from "@tanstack/db";

import type { WsFeedGroupMembership } from "@storyboard/web-client";
import { idx, rowsByIndex, store } from "@/store/store";
import type { FeedGroupMembership } from "@/store/types";

function transformWsFeedGroupMembership(r: WsFeedGroupMembership): FeedGroupMembership {
  return {
    id: r.id,
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? r.createdAt ?? null,
    groupId: r.groupId,
    feedId: r.feedId,
    workspaceId: r.workspaceId,
    enabled: r.enabled ? 1 : 0,
  };
}

export async function bulkUpsertFeedGroupMembership(records: WsFeedGroupMembership[]) {
  if (records.length === 0) return;
  // Keyed by (workspaceId, feedId) — the old conflict target; createdAt keeps its first value.
  store.upsert("feedGroupMembership", records.map(transformWsFeedGroupMembership), (existing, next) => ({
    ...existing,
    id: next.id,
    updatedAt: next.updatedAt,
    groupId: next.groupId,
    enabled: next.enabled,
  }));
}

export type SubscriptionStatusRow = {
  feedId: string;
  pushNotificationPreference: string;
  groupId: string | null;
  subscribed: boolean;
  title: string | null;
};

export const fetchSubscriptionStatus = async ({
  workspaceMembershipId,
}: {
  workspaceMembershipId: string;
}): Promise<SubscriptionStatusRow[]> => {
  const rows: SubscriptionStatusRow[] = [];
  for (const preference of rowsByIndex("feedPreference", idx.feedPreferencesByMembership, workspaceMembershipId)) {
    const feed = store.get("feed", preference.feedId);
    if (!feed) continue;
    // Mirrors the old join: read permission required, enabled not checked.
    const hasRead = rowsByIndex("permission", idx.permissionsByFeed, feed.id).some(
      (p) => p.workspaceMembershipId === workspaceMembershipId && p.name === "read",
    );
    if (!hasRead) continue;
    const groupMembership = rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByFeed, feed.id)[0] ?? null;
    rows.push({
      feedId: feed.id,
      pushNotificationPreference: preference.pushNotificationPreference,
      groupId: groupMembership?.groupId ?? null,
      subscribed: preference.pushNotificationPreference !== "none",
      title: feed.title,
    });
  }
  return rows;
};

export type GroupSubscriptionRow = {
  id: string;
  groupId: string;
  subscribed: boolean | null;
  feedId: string;
  title: string | null;
  pushNotificationPreference: string | null;
};

export const fetchAllGroupsWithSubscribedStatusQuery = async ({
  workspaceMembershipId,
  groupId,
}: {
  workspaceMembershipId: string;
  groupId: string;
}): Promise<GroupSubscriptionRow[]> => {
  const rows: GroupSubscriptionRow[] = [];
  for (const membership of rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByGroup, groupId)) {
    const feed = store.get("feed", membership.feedId);
    if (!feed) continue;
    // Mirrors the old join: read permission required, enabled not checked.
    const hasRead = rowsByIndex("permission", idx.permissionsByFeed, feed.id).some(
      (p) => p.workspaceMembershipId === workspaceMembershipId && p.name === "read",
    );
    if (!hasRead) continue;
    const preference =
      rowsByIndex("feedPreference", idx.feedPreferencesByFeed, feed.id).find(
        (p) => p.workspaceMembershipId === workspaceMembershipId,
      ) ?? null;
    rows.push({
      id: membership.id,
      groupId: membership.groupId,
      subscribed: preference ? preference.pushNotificationPreference !== "none" : null,
      feedId: membership.feedId,
      title: feed.title,
      pushNotificationPreference: preference?.pushNotificationPreference ?? null,
    });
  }
  return rows;
};

export type MyWorkspaceFeedGroupRow = { id: string; groupId: string; name: string };

/** Enabled, named groups with at least one feed the membership can read (EXISTS via distinct). */
export const myWorkspaceFeedGroupsQuery =
  (workspaceId: string, workspaceMembershipId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ group: store.collection("feedGroup") })
      .innerJoin({ groupMembership: store.collection("feedGroupMembership") }, ({ group, groupMembership }) =>
        eq(group.id, groupMembership.groupId),
      )
      .innerJoin({ permission: store.collection("permission") }, ({ groupMembership, permission }) =>
        eq(groupMembership.feedId, permission.feedId),
      )
      .where(({ group, permission }) =>
        and(
          eq(group.workspaceId, workspaceId),
          eq(group.enabled, 1),
          // group.name is a non-nullable string, so the old `!group.name` guard reduces to
          // rejecting the empty string.
          not(eq(group.name, "")),
          eq(permission.name, "read"),
          eq(permission.enabled, true),
          eq(permission.workspaceMembershipId, workspaceMembershipId),
        ),
      )
      .select(({ group }) => ({ id: group.id, groupId: group.id, name: group.name }))
      .distinct()
      .orderBy(({ group }) => group.name, { direction: "asc", nulls: "last" });
